import { useState, useEffect } from "react";
import { API } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell
} from "recharts";
import { 
  Star, Trophy, Target, Flame, Calendar, Clock, 
  BookOpen, CheckCircle, TrendingUp, Award, Medal,
  Zap, Brain, Lightbulb, GraduationCap, ChevronRight,
  RefreshCw
} from "lucide-react";

const COLORS = ['#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#EC4899'];

export default function ChildProgressView({ childId, childName }) {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [activity, setActivity] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (childId) {
      fetchAllData();
    }
  }, [childId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [progressRes, activityRes, achievementsRes] = await Promise.all([
        fetch(`${API}/parent/dashboard/child/${childId}/progress`, { credentials: 'include' }),
        fetch(`${API}/parent/dashboard/child/${childId}/activity?days=7`, { credentials: 'include' }),
        fetch(`${API}/parent/dashboard/child/${childId}/achievements`, { credentials: 'include' })
      ]);

      const progressData = await progressRes.json();
      const activityData = await activityRes.json();
      const achievementsData = await achievementsRes.json();

      if (progressData.success) setProgress(progressData);
      if (activityData.success) setActivity(activityData);
      if (achievementsData.success) setAchievements(achievementsData);
    } catch (error) {
      console.error("Error fetching child data:", error);
      toast.error("Failed to load progress data");
    } finally {
      setLoading(false);
    }
  };

  // Format activity data for charts
  const chartData = activity?.activity?.slice().reverse().map(day => ({
    date: new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' }),
    sessions: day.sessions,
    questions: day.questions,
    correct: day.correct,
    xp: day.xp_earned
  })) || [];

  // Calculate level progress
  const xp = progress?.overall_stats?.xp_points || 0;
  const level = progress?.overall_stats?.level || 1;
  const xpInLevel = xp % 100;
  const xpToNext = progress?.overall_stats?.xp_to_next_level || 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="progress-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="child-progress-view">
      {/* Header with XP and Level - Compact */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-4 text-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold">{childName}&apos;s Progress</h2>
            <p className="text-purple-200 text-xs">Learning Journey</p>
          </div>
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={fetchAllData}
            className="bg-white/20 hover:bg-white/30 text-white h-7 text-xs"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          {/* XP Card */}
          <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center min-w-0" data-testid="xp-card">
            <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mx-auto mb-1" />
            <p className="text-base sm:text-xl font-bold leading-tight truncate">{xp.toLocaleString()}</p>
            <span className="text-purple-200 text-[10px] sm:text-xs">XP</span>
          </div>

          {/* Level Card */}
          <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center min-w-0" data-testid="level-card">
            <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-300 mx-auto mb-1" />
            <p className="text-base sm:text-xl font-bold leading-tight truncate">{level}</p>
            <span className="text-purple-200 text-[10px] sm:text-xs">Level</span>
          </div>

          {/* Sessions Card */}
          <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center min-w-0" data-testid="sessions-card">
            <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-green-300 mx-auto mb-1" />
            <p className="text-base sm:text-xl font-bold leading-tight truncate">{progress?.overall_stats?.total_sessions || 0}</p>
            <span className="text-purple-200 text-[10px] sm:text-xs">Sessions</span>
          </div>

          {/* Accuracy Card */}
          <div className="bg-white/10 rounded-lg p-2 sm:p-3 text-center min-w-0" data-testid="accuracy-card">
            <Target className="h-4 w-4 sm:h-5 sm:w-5 text-blue-300 mx-auto mb-1" />
            <p className="text-base sm:text-xl font-bold leading-tight truncate">{progress?.overall_stats?.accuracy_percentage || 0}%</p>
            <span className="text-purple-200 text-[10px] sm:text-xs">Accuracy</span>
          </div>
        </div>
      </div>

      {/* Tabs for detailed views - Compact */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 h-9">
          <TabsTrigger value="overview" className="text-xs" data-testid="tab-overview">
            <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
            Activity
          </TabsTrigger>
          <TabsTrigger value="achievements" className="text-xs" data-testid="tab-achievements">
            <Award className="h-3.5 w-3.5 mr-1.5" />
            Achievements
          </TabsTrigger>
          <TabsTrigger value="subjects" className="text-xs" data-testid="tab-subjects">
            <BookOpen className="h-3.5 w-3.5 mr-1.5" />
            Subjects
          </TabsTrigger>
        </TabsList>

        {/* Activity Tab - Compact */}
        <TabsContent value="overview" className="space-y-4">
          {/* Weekly Activity Chart */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart className="h-4 w-4 text-purple-600" />
                Weekly Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3 px-4">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="colorXp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" stroke="#9ca3af" fontSize={10} />
                    <YAxis stroke="#9ca3af" fontSize={10} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1f2937', 
                        border: 'none', 
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '12px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="sessions" 
                      stroke="#8B5CF6" 
                      fillOpacity={1} 
                      fill="url(#colorSessions)" 
                      name="Sessions"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="xp" 
                      stroke="#10B981" 
                      fillOpacity={1} 
                      fill="url(#colorXp)" 
                      name="XP Earned"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-40 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Calendar className="h-8 w-8 mx-auto mb-1 text-gray-300" />
                    <p className="text-sm">No activity in the last 7 days</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Sessions - Compact */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Clock className="h-4 w-4 text-orange-600" />
                Recent Sessions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3 px-4">
              {progress?.recent_sessions?.length > 0 ? (
                <div className="space-y-2">
                  {progress.recent_sessions.slice(0, 4).map((session, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                      data-testid={`session-${idx}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <BookOpen className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{session.topic || session.subject || "Session"}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-purple-600 text-sm">+{session.xp_earned || 0} XP</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <Clock className="h-8 w-8 mx-auto mb-1 text-gray-300" />
                  <p>No sessions yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Achievements Tab - Compact */}
        <TabsContent value="achievements" className="space-y-4">
          {/* Earned Badges */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Trophy className="h-4 w-4 text-yellow-500" />
                Earned Badges ({achievements?.achievements?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3 px-4">
              {achievements?.achievements?.length > 0 ? (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {achievements.achievements.map((badge, idx) => (
                    <div 
                      key={idx}
                      className="p-3 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg border border-yellow-200 text-center"
                      data-testid={`badge-${badge.id}`}
                    >
                      <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                        {getBadgeIcon(badge.icon)}
                      </div>
                      <p className="font-medium text-xs text-gray-900 truncate">{badge.name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <Medal className="h-8 w-8 mx-auto mb-1 text-gray-300" />
                  <p>No badges earned yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Badges - Compact */}
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <Zap className="h-4 w-4 text-purple-500" />
                Next Achievements
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3 px-4">
              {achievements?.upcoming?.length > 0 ? (
                <div className="space-y-2">
                  {achievements.upcoming.slice(0, 3).map((badge, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-2 bg-gray-50 rounded-md"
                      data-testid={`upcoming-${badge.id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                        {getBadgeIcon(badge.icon, true)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-700 truncate">{badge.name}</p>
                        <Progress value={badge.progress} className="h-1.5 mt-1" />
                      </div>
                      <span className="text-xs text-purple-600 font-medium">
                        {Math.round(badge.progress)}%
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <p>All badges unlocked!</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subjects Tab - Compact */}
        <TabsContent value="subjects" className="space-y-4">
          <Card>
            <CardHeader className="py-3 px-4">
              <CardTitle className="flex items-center gap-2 text-base">
                <GraduationCap className="h-4 w-4 text-indigo-600" />
                Subject Mastery
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3 px-4">
              {progress?.mastery_by_subject && Object.keys(progress.mastery_by_subject).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(progress.mastery_by_subject).map(([subject, data]) => (
                    <div key={subject} className="space-y-1.5" data-testid={`subject-${subject}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-indigo-600" />
                          <span className="font-medium text-sm">{subject}</span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {data.mastered_concepts}/{data.total_concepts} mastered
                        </span>
                      </div>
                      <Progress value={data.mastery_percentage} className="h-2" />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{Math.round(data.mastery_percentage)}% complete</span>
                        <span>Avg: {Math.round(data.average_score * 100)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <BookOpen className="h-8 w-8 mx-auto mb-1 text-gray-300" />
                  <p>No subject data yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Helper function to get badge icons
function getBadgeIcon(iconName, locked = false) {
  const className = locked ? "h-6 w-6 text-gray-400" : "h-8 w-8 text-white";
  
  switch (iconName) {
    case 'star': return <Star className={className} />;
    case 'trophy': return <Trophy className={className} />;
    case 'medal': return <Medal className={className} />;
    case 'lightbulb': return <Lightbulb className={className} />;
    case 'brain': return <Brain className={className} />;
    case 'target': return <Target className={className} />;
    case 'calendar': return <Calendar className={className} />;
    case 'award': return <Award className={className} />;
    case 'flame': return <Flame className={className} />;
    default: return <Star className={className} />;
  }
}
